/**
 * Speech capture for the web client.
 *
 * docs/03 §1.5 says mobile is the capture surface and web is the planning
 * surface, because Safari's speech recognition is unreliable. That still holds
 * — this is an addition for the browsers where it genuinely works, not a
 * replacement for the iOS app's on-device recognition.
 *
 *   Chrome / Edge, desktop and Android : works well
 *   Safari                             : partial and flaky — we hide the button
 *   Firefox                            : no support at all — we hide the button
 *
 * Privacy, stated plainly because it differs from the phone: Chrome sends audio
 * to Google's servers for recognition. The iOS app uses on-device recognition
 * and nothing leaves the phone. The UI says so.
 */

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

type Ctor = new () => SpeechRecognitionLike;

function ctor(): Ctor | null {
  const w = window as unknown as Record<string, Ctor | undefined>;
  return w["SpeechRecognition"] ?? w["webkitSpeechRecognition"] ?? null;
}

/** Whether the browser exposes recognition at all. */
export function isSupported(): boolean {
  return ctor() !== null;
}

/**
 * Safari supports recognition but is more fragile than Chrome: it needs a
 * direct user gesture, it stops on its own more eagerly, and it sometimes ends
 * without firing a result. It is offered anyway — the error paths handle it —
 * but the UI says so once rather than pretending the two are equivalent.
 */
export function isFragile(): boolean {
  const ua = navigator.userAgent;
  return /^((?!chrome|android|crios|edg).)*safari/i.test(ua);
}

export interface Listener {
  stop(): void;
}

export interface Handlers {
  /** Fires repeatedly as words arrive. */
  onInterim(text: string): void;
  /** Fires once when recognition settles. */
  onFinal(text: string): void;
  onError(code: string): void;
  onEnd(): void;
}

export function listen(h: Handlers, lang = navigator.language || "en-US"): Listener | null {
  const C = ctor();
  if (!C) return null;

  const rec = new C();
  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = true;

  let finalText = "";
  rec.onresult = (e) => {
    let interim = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i]!;
      const text = r[0]?.transcript ?? "";
      if (r.isFinal) finalText += text;
      else interim += text;
    }
    h.onInterim((finalText + interim).trim());
  };
  rec.onerror = (e) => h.onError(e.error);
  rec.onend = () => {
    const t = finalText.trim();
    if (t) h.onFinal(t);
    h.onEnd();
  };

  try {
    rec.start();
  } catch {
    return null;
  }
  return { stop: () => rec.stop() };
}
